import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core'
import { CommonModule } from '@angular/common'
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu'
import { TranslateModule } from '@ngx-translate/core'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { ProjectorsEntity } from '@galabid/gb-dashboard/projector/data-access'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbPresenterCanvasComponent } from '../gb-presenter-canvas/gb-presenter-canvas.component'

@Component({
  selector: 'gb-presenter-slides',
  imports: [
    CommonModule,
    DragDropModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    GbPresenterCanvasComponent,
  ],
  templateUrl: './gb-presenter-slides.component.html',
  styleUrl: './gb-presenter-slides.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbPresenterSlidesComponent {
  @Input() slides: ProjectorsEntity[] | null = []
  @Input() currentSlideId: string | null | number | undefined = null
  @Input() aspectRatio = '16:9'

  @Output() slideSelected = new EventEmitter<string>()
  @Output() slideAdded = new EventEmitter<void>()
  @Output() slideDeleted = new EventEmitter<string>()
  @Output() slideDuplicated = new EventEmitter<string>()
  @Output() slideRenamed = new EventEmitter<string>()
  @Output() slideReordered = new EventEmitter<{ fromIndex: number; toIndex: number }>()

  onSlideClick(slideId: string): void {
    this.slideSelected.emit(slideId)
  }

  onAddSlide(): void {
    this.slideAdded.emit()
  }

  onDeleteSlide(slideId: string, event: Event): void {
    event.stopPropagation()
    this.slideDeleted.emit(slideId)
  }

  onDuplicateSlide(slideId: string, event: Event): void {
    event.stopPropagation()
    this.slideDuplicated.emit(slideId)
  }

  onSlideDrop(event: CdkDragDrop<ProjectorsEntity[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return
    }

    this.slideReordered.emit({ fromIndex: event.previousIndex, toIndex: event.currentIndex })
  }

  onRenameSlide(slideId: string, event: Event, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation()
    menuTrigger.closeMenu()
    this.slideRenamed.emit(slideId)
  }

  isCurrentSlide(slideId: string): boolean {
    return this.currentSlideId === slideId
  }

  getAspectRatioStyle(): { [key: string]: string } {
    const [width, height] = this.aspectRatio.split(':').map(Number)
    const aspectRatio = width / height
    return {
      'aspect-ratio': aspectRatio.toString(),
    }
  }

  trackBySlideId(index: number, slide: ProjectorsEntity): string {
    return slide.uuid
  }

  constructor(private colorResolver: GbPresenterColorResolverService) {}

  getResolvedColor(color: string | null | undefined): string | null {
    return this.colorResolver.resolve(color) || null
  }
}
